/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// Envision - sibling product options component
(function () {
  let element = document.querySelector('.product-info .product-options .sibling-product-options');
  if (element !== null) {
    element.addEventListener('change', (event) => {
      if (event.target && event.target.matches('input[type="radio"]')) {
        let optionUrl = event.target.value;
        if (optionUrl.includes("/products/")) {
          window.location.href = optionUrl;
        }
      }
    })
  }
})();

// Envision - lightbox component scripts
(function () {
  window.globalVenoboxInstance = new VenoBox({
    selector: '[data-lightbox]'
  });

  document.addEventListener('click', (event) => {
    const lightboxCloseButton = event.target.closest('[data-lightbox-close]');
    if (lightboxCloseButton) {
      event.preventDefault();
      window.globalVenoboxInstance.close();
    }
  });

  const reinitializeVenobox = () => {
    window.globalVenoboxInstance.close();
    window.globalVenoboxInstance = new VenoBox({
      selector: '[data-lightbox]'
    });
  }

  document.addEventListener('on:cart:refresh', reinitializeVenobox);
  document.addEventListener('on:line-item:change', reinitializeVenobox);
})();

// Envision - Variant Messages
(function () {
  function showVariantMessages(activeVariant) {
    let messages = document.querySelectorAll('.variant-messages .variant-message[data-message-variant-id]');

    messages.forEach((message) => {
      message.classList.add('hidden');

      if (message.matches(`[data-message-variant-id="${activeVariant.id}"]`)){
        message.classList.remove('hidden');
      }
    });
  }
  
  if (document.querySelectorAll('.variant-messages .variant-message[data-message-variant-id]').length > 0) {
    document.addEventListener('on:variant:change', (event) => {
      showVariantMessages(event.detail.variant);
    });
  }
})();

// Envision - MSRP pricing variant messages
(function () {
  function showMSRPMessages(activeVariant) {
    let messages = document.querySelectorAll('.product-info__msrp .msrp-price[data-msrp-variant-id]');

    messages.forEach((message) => {
      message.classList.add('hidden');

      if (message.matches(`[data-msrp-variant-id="${activeVariant.id}"]`)){
        message.classList.remove('hidden');
      }
    });
  }
  
  if (document.querySelectorAll('.product-info__msrp .msrp-price[data-msrp-variant-id]').length > 0) {
    document.addEventListener('on:variant:change', (event) => {
      showMSRPMessages(event.detail.variant);
    });
  }
})();

// Envision - on collection pages, switch to list view on extra-small screens
(function() {
  const handleCollectionViewResize = () => {
    let productsToolbar = document.querySelector(".cc-collection-products products-toolbar");
    if (productsToolbar && typeof productsToolbar["handleLayoutChange"] === "function") {
      if (!window.matchMedia(theme.mediaQueries.xsm).matches) {
        productsToolbar.handleLayoutChange(null, "list", false);
      } else {
        const layout = theme.storageUtil.get('products-layout');
        if (!layout || (layout && layout === "grid")) {
          productsToolbar.handleLayoutChange(null, "grid", false);
        }
      }
    }
  };

  if (document.querySelector(".cc-collection-products products-toolbar")) {
    window.addEventListener('on:breakpoint-change', (event) => {
      handleCollectionViewResize();
    });

    handleCollectionViewResize();
  }
})();

// Update mobile sku number in PDP
(function() {
  if (skuElement = document.querySelector(".product-details-mobile .product-sku__value")) {
    document.addEventListener('on:variant:change', (event) => {
      skuElement.textContent = event.detail.variant.sku;
    });
  }
})();

// Link PDP rating stars -> scroll down to reviews element and expand it
(function() {
  if ((linkElements = document.querySelectorAll(".product-info__rating")) && (linkTarget = document.querySelector("details-disclosure .disclosure--row:has(.custom-reviews)"))) {
    linkTarget.style.scrollMarginTop = "150px";
    for (i = 0; i < linkElements.length; i++) {
      linkElements[i].setAttribute('href', "#");    
      linkElements[i].addEventListener('click', (event) => {
        event.preventDefault();
        linkTarget.scrollIntoView({behavior: 'smooth'});

        // Open reviews tab
        if (!linkTarget.open) {
          let panel = linkTarget.querySelector('summary').nextElementSibling;
          panel.style.height = '0';
          linkTarget.open = true;
          panel.style.height = `${panel.scrollHeight}px`;
        };
      });
    };
  };
})();

// Envision - generic tooltip component
// Source: https://gist.github.com/joeRob2468/19ef4ace6359649e6f38c1c279ef3916
(function() {
  class Tooltip extends HTMLElement {
    connectedCallback() {
      this.setup();
    }

    handleDropdownPosition() {
      const screenPadding = 0; // screen padding is moved to an actual padding value on the dropdown element using CSS
      
      this.dropdown.style = '';

      const placeholderRect = this.placeholder.getBoundingClientRect();
      const dropdownRect = this.dropdown.getBoundingClientRect();
      
      const dropdownRightX = dropdownRect.x + dropdownRect.width;
      const placeholderRightX = placeholderRect.x + placeholderRect.width;
      
      var viewportHeight;
      var viewportWidth;
      if (document.compatMode === 'BackCompat') {
          viewportHeight = document.body.clientHeight;
          viewportWidth = document.body.clientWidth;
      } else {
          viewportHeight = document.documentElement.clientHeight;
          viewportWidth = document.documentElement.clientWidth;
      }

      if (dropdownRect.x < screenPadding) {
        this.dropdown.style.left = '0';
        this.dropdown.style.right = 'auto';
        this.dropdown.style.transform = `translateX(${-placeholderRect.x + screenPadding}px)`;
      } else if (dropdownRightX > viewportWidth - screenPadding) {
        this.dropdown.style.left = 'auto';
        this.dropdown.style.right = '0';
        this.dropdown.style.transform = `translateX(${(viewportWidth - placeholderRightX) - screenPadding}px)`;
      }
    }

    toggle(e) {
      e.preventDefault();
      if (this.classList.contains('tooltip--open')) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      this.handleDropdownPosition();
      this.classList.add('tooltip--open');
    }

    close() {
      this.classList.remove('tooltip--open');
    }

    setup() {
      this.placeholder = this.querySelector('[data-tooltip-target]');
      this.dropdown = this.querySelector('[data-tooltip-element]');

      this.placeholder.addEventListener('mouseover', () => this.handleDropdownPosition());
      this.placeholder.addEventListener('click', (e) => this.toggle(e));
    }
  }

  function dismissAllTooltips(event) {
    if (typeof event.target.closest !== 'function') return;
    const currentTooltip = event.target.closest('tooltip-component');

    document.querySelectorAll('.tooltip--open').forEach(tooltip => {
      if (tooltip === currentTooltip) return;

      tooltip.classList.remove('tooltip--open');
    });
  }

  customElements.define('tooltip-component', Tooltip);
  document.addEventListener('click', e => dismissAllTooltips(e));
})();

// Highlight article table of contents item
(function() {
  if ((itemElements = document.querySelectorAll(".contents-item")).length > 0 ) {
    // itemElements = Table of contents items
    // observedElements = H2 elements
    observedElements = [];
    itemElements.forEach((item) => {
      try {
        observedElements.push(document.querySelector('#' + CSS.escape(item.id.replace('contents-item__', ''))));
      } catch (e) {
        console.log(e);
      }
    });

    const ioOptions = {
      root: null,
      rootMargin: "-45% 0% -45% 0%"
    };
    
    let articleElementsDictionary = new Object()
    observedElements.forEach((header) => {
      let siblingArray = [];
      
      if (header.nextElementSibling != null){
        let nextSibling = header.nextElementSibling;
        while(nextSibling.nextElementSibling != null && nextSibling.tagName.toLowerCase() != 'h2'){
          siblingArray.push(nextSibling);
          nextSibling = nextSibling.nextElementSibling;
        };
        articleElementsDictionary[header.id] = siblingArray;
      };
    });
    if (observedElements[0].previousElementSibling != null){
      let previousSibling = observedElements[0].previousElementSibling
      let validLoop = true
      while (validLoop){
        articleElementsDictionary[observedElements[0].id].push(previousSibling)
        if (previousSibling.previousElementSibling != null){
          previousSibling = previousSibling.previousElementSibling
        } else {
          validLoop = false
        }
      }
    }

    // currentlyActive - Currently observed h2
    let currentlyActive = observedElements[0];
    let currentlyActiveHeader = observedElements[0];

    // itemElement - Matching table of contents item for active h2
    let itemElement = document.querySelector('#contents-item__' + currentlyActiveHeader.id);

    const contentsObserver = new IntersectionObserver(entries => {
      // Remove last active class
      itemElement = document.querySelector('#contents-item__' + currentlyActiveHeader.id);
      itemElement.classList.remove('active');

      // Update active to new element
      currentlyActive = entries[entries.length - 1].target;

      // Add new active class
      if (currentlyActive.tagName.toLowerCase() == "h2") {
        currentlyActiveHeader = currentlyActive;
      } else {
        observedElements.forEach((header) => {
          articleElementsDictionary[header.id].forEach((siblingElement) => {
            if(siblingElement == currentlyActive){
              currentlyActiveHeader = header;
            };
          });
        });
      };

      itemElement = document.querySelector('#contents-item__' + currentlyActiveHeader.id);
      itemElement.classList.add('active');
    }, ioOptions);

    observedElements.forEach((header) => {
      articleElementsDictionary[header.id].forEach((element) => {
        contentsObserver.observe(element);
      });
    });

    itemElements.forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        
        document.querySelector('#' + CSS.escape(item.id.replace('contents-item__', ''))).scrollIntoView({block: "start", behavior: "smooth"});
        setTimeout(() => {
          window.location.hash = '#' + item.id.replace('contents-item__', '');
        }, 750);
      });
    });

    let url_target = window.location.href.split("#");
    if (url_target.length > 1) {
      target_element = document.querySelector("#" + url_target[1]);
      target_element.scrollIntoView({block: "start", behavior: "smooth"})
    };
  }
})();

// Envision - Button link to open helpscout
(function () {
  let buttonList = null;
  if (buttonList = document.querySelectorAll('.btn')) {
    buttonList.forEach(buttonElement => {      
      if (buttonElement.href?.includes('#open-helpscout')) {
        buttonElement.addEventListener('click', (event) => {
          if (window.Beacon == undefined) {
            let a = document.querySelector("#helpscout-facade-button");
            if (a != undefined) {
              // normal install script
              !function(e,t,n){function a(){var e=t.getElementsByTagName("script")[0],n=t.createElement("script");n.type="text/javascript",n.defer=true,n.src="https://beacon-v2.helpscout.net",e.parentNode.insertBefore(n,e)}if(e.Beacon=n=function(t,n,a){e.Beacon.readyQueue.push({method:t,options:n,data:a})},n.readyQueue=[],"complete"===t.readyState)return a();e.attachEvent?e.attachEvent("onload",a):e.addEventListener("load",a,!1)}(window,document,window.Beacon||function(){});
              window.Beacon('init', '96774103-42ce-4d84-bb82-43fb34c03b28')

              // open chat and clean up facade
              window.Beacon('on', 'ready', function() {
                window.Beacon('open')
                a.parentNode.removeChild(a)
              })
            }
          } else {
            window.Beacon('open');
          };
        });
      };
    });
  };
})();